import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faChevronDown,
  faPaperPlane,
  faPen,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { getToken } from "../../utils/auth";
import "./modales.css";

const TIPOS_POPUP = ["PC", "CEL", "TABLET"];

const RECOMENDACIONES_POR_TIPO = {
  PC: "1200 × 675 px (16:9)",
  CEL: "720 × 960 px (3:4)",
  TABLET: "960 × 720 px (4:3)",
};

const MAX_ARCHIVO_BYTES = 10 * 1024 * 1024;
// El navegador no puede decodificar imágenes gigantes (p. ej. exportes a 300 dpi):
// se guardan bien pero el Pop-Up nunca se muestra. Limitamos el lado mayor.
const MAX_LADO_PX = 4000;

const leerDimensiones = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

// Devuelve un mensaje de error o null si la imagen es válida.
const validarImagen = async (file) => {
  if (!file.type.startsWith("image/")) return "Seleccioná un archivo de imagen.";
  if (file.size > MAX_ARCHIVO_BYTES) return "El peso máximo permitido es 10 MB.";
  const dims = await leerDimensiones(file);
  if (!dims) {
    return "El navegador no pudo abrir la imagen. Suele pasar con imágenes de dimensiones enormes: exportala al tamaño recomendado.";
  }
  if (dims.width > MAX_LADO_PX || dims.height > MAX_LADO_PX) {
    return `La imagen mide ${dims.width} × ${dims.height} px y no se va a poder mostrar. Exportala al tamaño recomendado (por ej. PC ${RECOMENDACIONES_POR_TIPO.PC}).`;
  }
  return null;
};

const emptyPopupForm = () => ({
  nombre: "",
  tipo: "PC",
  fechaInicio: "",
  horaInicio: "00:00",
  fechaFin: "",
  horaFin: "23:59",
});

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const combineFechaHora = (fecha, hora) => {
  if (!fecha) return null;
  return `${fecha}T${hora || "00:00"}:00`;
};

const splitFechaHora = (value) => {
  if (!value) return { fecha: "", hora: "00:00" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { fecha: "", hora: "00:00" };
  const pad = (n) => String(n).padStart(2, "0");
  return {
    fecha: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    hora: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const formatFechaHora = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const fecha = date.toLocaleDateString("es-AR");
  const hora = date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${fecha} ${hora}`;
};

const splitAsuntoMensaje = (texto = "") => {
  const raw = String(texto || "");
  const idx = raw.indexOf("\n");
  if (idx === -1) {
    return { asunto: "", mensaje: raw };
  }
  return { asunto: raw.slice(0, idx), mensaje: raw.slice(idx + 1) };
};

const fireExito = (titulo, texto) =>
  Swal.fire({
    html: `
      <div class="crm-modales__swal-icon"><span>✓</span></div>
      <h3 class="crm-modales__swal-title">${titulo}</h3>
      <p class="crm-modales__swal-text">${texto}</p>
    `,
    showConfirmButton: true,
    confirmButtonText: "OK",
    buttonsStyling: false,
    customClass: {
      popup: "crm-modales__swal-popup",
      confirmButton: "crm-modales__swal-confirm",
    },
  });

export default function ModalesPanel() {
  // ---------- Pop-Ups: listado ----------
  const [popups, setPopups] = useState([]);
  const [loadingPopups, setLoadingPopups] = useState(true);
  const [errorPopups, setErrorPopups] = useState("");
  const [deletingPopupId, setDeletingPopupId] = useState(null);

  // ---------- Pop-Ups: creación (card inline) ----------
  const [savingPopup, setSavingPopup] = useState(false);
  const [popupForm, setPopupForm] = useState(emptyPopupForm());
  const [popupArchivo, setPopupArchivo] = useState(null);
  const fileInputRef = useRef(null);

  // ---------- Pop-Ups: edición (modal) ----------
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyPopupForm());
  const [editImagenActual, setEditImagenActual] = useState(null); // base64 data-uri real, o null
  const [editArchivoNuevo, setEditArchivoNuevo] = useState(null); // File local, si se reemplaza
  const [editPreviewLocal, setEditPreviewLocal] = useState(null); // object URL del archivo nuevo
  const editFileInputRef = useRef(null);

  const cargarPopups = async ({ silent = false } = {}) => {
    if (!silent) setLoadingPopups(true);
    if (!silent) setErrorPopups("");
    try {
      const res = await fetch(process.env.REACT_APP_API_POPUP_LISTAR);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPopups(
        Array.isArray(data)
          ? [...data].sort((a, b) => Number(b.id) - Number(a.id))
          : []
      );
    } catch {
      if (!silent) setErrorPopups("No pudimos cargar los Pop-Ups.");
    } finally {
      if (!silent) setLoadingPopups(false);
    }
  };

  useEffect(() => {
    cargarPopups();
  }, []);

  useEffect(
    () => () => {
      if (editPreviewLocal) URL.revokeObjectURL(editPreviewLocal);
    },
    [editPreviewLocal]
  );

  const resetPopupForm = () => {
    setPopupForm(emptyPopupForm());
    setPopupArchivo(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePopupArchivoChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0] || null;
    if (!file) return;

    const error = await validarImagen(file);
    if (error) {
      Swal.fire("Imagen inválida", error, "warning");
      input.value = "";
      return;
    }

    setPopupArchivo(file);
  };

  const handleCrearPopup = async (event) => {
    event.preventDefault();
    if (savingPopup) return;

    if (!popupForm.nombre.trim() || !popupForm.tipo || !popupForm.fechaInicio || !popupForm.fechaFin) {
      Swal.fire("Faltan datos", "Completá nombre, tipo, fecha de inicio y fecha de finalización.", "warning");
      return;
    }

    const fechaInicio = combineFechaHora(popupForm.fechaInicio, popupForm.horaInicio);
    const fechaFin = combineFechaHora(popupForm.fechaFin, popupForm.horaFin);

    if (new Date(fechaFin) < new Date(fechaInicio)) {
      Swal.fire("Fechas inválidas", "La fecha/hora de finalización no puede ser anterior a la de inicio.", "warning");
      return;
    }

    if (!popupArchivo) {
      Swal.fire("Falta la imagen", "Seleccioná un archivo para el Pop-Up.", "warning");
      return;
    }

    setSavingPopup(true);
    try {
      const imagenBase64 = await toBase64(popupArchivo);
      const formData = new FormData();
      formData.append("Nombre", popupForm.nombre.trim());
      formData.append("ImagenBase64", imagenBase64);
      formData.append("Tipo", popupForm.tipo);
      formData.append("FechaInicio", fechaInicio);
      formData.append("FechaFin", fechaFin);

      const res = await fetch(process.env.REACT_APP_API_POPUP_SUBIR, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error();
      const creado = await res.json();

      const nuevoPopup = {
        id: creado.id,
        nombre: popupForm.nombre.trim(),
        tipo: popupForm.tipo,
        fechaInicio,
        fechaFin,
        activoHoy: new Date(fechaInicio) <= new Date() && new Date(fechaFin) >= new Date(),
      };

      // Mostramos el alta inmediatamente; la sincronización completa queda en segundo plano.
      setPopups((prev) => [nuevoPopup, ...prev.filter((item) => item.id !== nuevoPopup.id)]);

      resetPopupForm();
      await fireExito("Pop-Up creado con éxito", "El nuevo modal ya está disponible en el listado.");
      cargarPopups({ silent: true });
    } catch {
      Swal.fire("Error", "No se pudo crear el Pop-Up.", "error");
    } finally {
      setSavingPopup(false);
    }
  };

  const cerrarModalEdicion = () => {
    setEditModalOpen(false);
    setEditId(null);
    setEditForm(emptyPopupForm());
    setEditImagenActual(null);
    setEditArchivoNuevo(null);
    if (editPreviewLocal) URL.revokeObjectURL(editPreviewLocal);
    setEditPreviewLocal(null);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const abrirEdicionPopup = async (popup) => {
    setEditModalOpen(true);
    setEditLoading(true);
    setEditId(popup.id);
    setEditArchivoNuevo(null);
    setEditPreviewLocal(null);

    try {
      const res = await fetch(`/api/popup/${popup.id}`);
      if (!res.ok) throw new Error();
      const detalle = await res.json();

      const inicio = splitFechaHora(detalle.fechaInicio);
      const fin = splitFechaHora(detalle.fechaFin);

      setEditForm({
        nombre: detalle.nombre || "",
        tipo: String(detalle.tipo || "PC").toUpperCase(),
        fechaInicio: inicio.fecha,
        horaInicio: inicio.hora,
        fechaFin: fin.fecha,
        horaFin: fin.hora,
      });
      setEditImagenActual(detalle.imagenBase64 || null);
    } catch {
      Swal.fire("Error", "No pudimos abrir el Pop-Up para editar.", "error");
      cerrarModalEdicion();
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditArchivoChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0] || null;
    if (!file) return;

    const error = await validarImagen(file);
    if (error) {
      Swal.fire("Imagen inválida", error, "warning");
      input.value = "";
      return;
    }

    if (editPreviewLocal) URL.revokeObjectURL(editPreviewLocal);
    setEditArchivoNuevo(file);
    setEditPreviewLocal(URL.createObjectURL(file));
  };

  const handleGuardarEdicionPopup = async (event) => {
    event.preventDefault();
    if (editSaving || !editId) return;

    if (!editForm.nombre.trim() || !editForm.tipo || !editForm.fechaInicio || !editForm.fechaFin) {
      Swal.fire("Faltan datos", "Completá nombre, tipo, fecha de inicio y fecha de finalización.", "warning");
      return;
    }

    const fechaInicio = combineFechaHora(editForm.fechaInicio, editForm.horaInicio);
    const fechaFin = combineFechaHora(editForm.fechaFin, editForm.horaFin);

    if (new Date(fechaFin) < new Date(fechaInicio)) {
      Swal.fire("Fechas inválidas", "La fecha/hora de finalización no puede ser anterior a la de inicio.", "warning");
      return;
    }

    setEditSaving(true);
    try {
      const body = {
        id: editId,
        nombre: editForm.nombre.trim(),
        tipo: editForm.tipo,
        fechaInicio,
        fechaFin,
      };

      // Solo se envía imagen si el usuario eligió una nueva; si no, el backend conserva la actual.
      if (editArchivoNuevo) {
        body.imagenBase64 = await toBase64(editArchivoNuevo);
      }

      const res = await fetch(process.env.REACT_APP_API_POPUP_EDITAR, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();

      cerrarModalEdicion();
      await cargarPopups();
      Swal.fire("Actualizado", "El Pop-Up se modificó correctamente.", "success");
    } catch {
      Swal.fire("Error", "No se pudo guardar el Pop-Up. Los datos ingresados se conservan.", "error");
    } finally {
      setEditSaving(false);
    }
  };

  const handleEliminarPopup = async (popup) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar Pop-Up?",
      text: `${popup.nombre || popup.tipo || "-"} · ${popup.tipo || "-"}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    setDeletingPopupId(popup.id);
    try {
      const res = await fetch(process.env.REACT_APP_API_POPUP_ELIMINAR, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: popup.id }),
      });
      if (!res.ok) throw new Error();
      Swal.fire("Eliminado", "El Pop-Up fue eliminado.", "success");
      if (editId === popup.id) cerrarModalEdicion();
      await cargarPopups();
    } catch {
      Swal.fire("Error", "No se pudo eliminar el Pop-Up.", "error");
    } finally {
      setDeletingPopupId(null);
    }
  };

  // ---------- Notificaciones / Para aliados ----------
  const [notificaciones, setNotificaciones] = useState([]);
  const [loadingNotificaciones, setLoadingNotificaciones] = useState(true);
  const [errorNotificaciones, setErrorNotificaciones] = useState("");
  const [notifForm, setNotifForm] = useState({
    asunto: "",
    mensaje: "",
    enviarPush: true,
    cuitDestino: "",
  });
  const [sendingNotif, setSendingNotif] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [savingNotifEdit, setSavingNotifEdit] = useState(false);
  const [deletingNotifId, setDeletingNotifId] = useState(null);
  const [paraAliadosExpanded, setParaAliadosExpanded] = useState(false);

  const cargarNotificaciones = async () => {
    setLoadingNotificaciones(true);
    setErrorNotificaciones("");
    try {
      const res = await fetch(process.env.REACT_APP_API_NOTIFICACIONES);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const aliados = Array.isArray(data)
        ? data.filter((item) => String(item.tipoUsuario || "").trim() === "Aliado")
        : [];
      setNotificaciones(aliados);
    } catch {
      setErrorNotificaciones("No pudimos cargar las publicaciones para aliados.");
    } finally {
      setLoadingNotificaciones(false);
    }
  };

  useEffect(() => {
    cargarNotificaciones();
  }, []);

  const handleEnviarNotificacion = async (event) => {
    event.preventDefault();
    if (sendingNotif) return;

    if (!notifForm.asunto.trim() || !notifForm.mensaje.trim()) {
      Swal.fire("Faltan datos", "Completá el asunto y el mensaje.", "warning");
      return;
    }
    const cuitDestino = notifForm.cuitDestino.replace(/\D/g, "");
    if (notifForm.enviarPush && cuitDestino && cuitDestino.length !== 11) {
      Swal.fire("CUIT inválido", "El CUIT destino debe tener 11 dígitos.", "warning");
      return;
    }

    setSendingNotif(true);
    try {
      const token = getToken();
      const res = await fetch(process.env.REACT_APP_API_NOTIFICACION_CREAR, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          TipoUsuario: "Aliado",
          Noticia1: `${notifForm.asunto.trim()}\n${notifForm.mensaje.trim()}`,
          Token: token,
          Fecha: new Date().toISOString().split("T")[0],
          EnviarPush: notifForm.enviarPush,
          CuitDestino: cuitDestino || null,
        }),
      });
      if (!res.ok) throw new Error();
      const resultado = await res.json();
      const detallePush = resultado.push
        ? ` Push enviadas: ${resultado.push.enviados}; fallidas: ${resultado.push.fallidos}.`
        : "";
      const detalleError = resultado.pushError ? ` ${resultado.pushError}` : "";
      Swal.fire(
        resultado.pushError ? "Publicada con aviso" : "Enviado",
        `La notificación se publicó para aliados.${detallePush}${detalleError}`,
        resultado.pushError ? "warning" : "success"
      );
      setNotifForm({ asunto: "", mensaje: "", enviarPush: true, cuitDestino: "" });
      await cargarNotificaciones();
    } catch {
      Swal.fire("Error", "No se pudo enviar la notificación.", "error");
    } finally {
      setSendingNotif(false);
    }
  };

  const abrirEdicionNotif = (item) => {
    const { asunto, mensaje } = splitAsuntoMensaje(item.noticia1);
    setEditingNotif({ id: item.id, asunto, mensaje });
  };

  const handleGuardarEdicionNotif = async (event) => {
    event.preventDefault();
    if (!editingNotif || savingNotifEdit) return;

    if (!editingNotif.mensaje.trim()) {
      Swal.fire("Faltan datos", "El mensaje no puede estar vacío.", "warning");
      return;
    }

    setSavingNotifEdit(true);
    try {
      const token = getToken();
      const contenido = editingNotif.asunto.trim()
        ? `${editingNotif.asunto.trim()}\n${editingNotif.mensaje.trim()}`
        : editingNotif.mensaje.trim();

      const res = await fetch(process.env.REACT_APP_API_NOTIFICACION_EDITAR, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Noticia1: contenido, Token: token, Id: editingNotif.id }),
      });
      if (!res.ok) throw new Error();
      Swal.fire("Actualizado", "La publicación se modificó correctamente.", "success");
      setEditingNotif(null);
      await cargarNotificaciones();
    } catch {
      Swal.fire("Error", "No se pudo guardar la publicación. Los datos ingresados se conservan.", "error");
    } finally {
      setSavingNotifEdit(false);
    }
  };

  const handleEliminarNotif = async (item) => {
    const { asunto, mensaje } = splitAsuntoMensaje(item.noticia1);
    const confirm = await Swal.fire({
      title: "¿Eliminar publicación?",
      text: asunto || mensaje.slice(0, 80),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    setDeletingNotifId(item.id);
    try {
      const token = getToken();
      const res = await fetch(process.env.REACT_APP_API_NOTIFICACION_ELIMINAR, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, Token: token }),
      });
      if (!res.ok) throw new Error();
      Swal.fire("Eliminado", "La publicación fue eliminada.", "success");
      await cargarNotificaciones();
    } catch {
      Swal.fire("Error", "No se pudo eliminar la publicación.", "error");
    } finally {
      setDeletingNotifId(null);
    }
  };

  const dateLabel = new Date().toLocaleDateString("es-AR");
  const timeLabel = new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });
  const visibleNotificaciones = paraAliadosExpanded ? notificaciones : notificaciones.slice(0, 3);
  const editPreview = editPreviewLocal || editImagenActual;

  return (
    <div className="crm-modales">
      <div className="crm-postventa-altas-header">
        <div className="crm-postventa-altas-header-copy">
          <span className="crm-postventa-kicker">CRM POSTVENTAS</span>
          <h2>Modales</h2>
        </div>
        <div className="crm-postventa-altas-header-clock">
          <span>{dateLabel}</span>
          <span className="crm-postventa-altas-header-separator" />
          <span>{timeLabel}</span>
        </div>
      </div>

      {/* ---------- Crear Pop-Up ---------- */}
      <section className="crm-modales__card">
        <div className="crm-modales__card-header">
          <h3>Crear Pop-Up</h3>
          <button type="button" className="crm-modales__btn-primary" onClick={handleCrearPopup} disabled={savingPopup}>
            {savingPopup ? "Guardando..." : "Guardar y crear"}
          </button>
        </div>

        <form className="crm-modales__popup-form" onSubmit={handleCrearPopup}>
          <div className="crm-modales__popup-fields">
            <label className="crm-modales__field">
              <span>Nombre del modal</span>
              <input
                type="text"
                value={popupForm.nombre}
                onChange={(event) => setPopupForm((prev) => ({ ...prev, nombre: event.target.value }))}
              />
            </label>

            <label className="crm-modales__field">
              <span>Tipo</span>
              <select
                value={popupForm.tipo}
                onChange={(event) => setPopupForm((prev) => ({ ...prev, tipo: event.target.value }))}
              >
                {TIPOS_POPUP.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </select>
            </label>

            <label className="crm-modales__field">
              <span>Fecha de inicio</span>
              <input
                type="date"
                value={popupForm.fechaInicio}
                onChange={(event) => setPopupForm((prev) => ({ ...prev, fechaInicio: event.target.value }))}
              />
            </label>

            <label className="crm-modales__field">
              <span>Hora de inicio</span>
              <input
                type="time"
                value={popupForm.horaInicio}
                onChange={(event) => setPopupForm((prev) => ({ ...prev, horaInicio: event.target.value }))}
              />
            </label>

            <label className="crm-modales__field">
              <span>Fecha de finalización</span>
              <input
                type="date"
                value={popupForm.fechaFin}
                onChange={(event) => setPopupForm((prev) => ({ ...prev, fechaFin: event.target.value }))}
              />
            </label>

            <label className="crm-modales__field">
              <span>Hora de finalización</span>
              <input
                type="time"
                value={popupForm.horaFin}
                onChange={(event) => setPopupForm((prev) => ({ ...prev, horaFin: event.target.value }))}
              />
            </label>
          </div>

          <div className="crm-modales__upload-column">
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePopupArchivoChange} />
            <button type="button" className="crm-modales__upload-box" onClick={() => fileInputRef.current?.click()}>
              <FontAwesomeIcon icon={faArrowUpFromBracket} />
              <strong>Seleccionar archivo</strong>
              <span>Peso máximo 10 MB</span>
            </button>
            {popupArchivo ? <div className="crm-modales__file-name">{popupArchivo.name}</div> : null}
            <p className="crm-modales__upload-note">Cargá una imagen acorde al tipo seleccionado.</p>
            <ul className="crm-modales__upload-hints">
              {TIPOS_POPUP.map((tipo) => (
                <li key={tipo}>
                  {tipo} {RECOMENDACIONES_POR_TIPO[tipo]}
                </li>
              ))}
            </ul>
          </div>
        </form>
      </section>

      {/* ---------- Listado ---------- */}
      <section className="crm-modales__card crm-modales__list-card">
        <div className="crm-modales__columns crm-modales__columns--con-nombre">
          <span>Nombre del modal</span>
          <span>Tipo</span>
          <span>ID</span>
          <span>Inicio</span>
          <span>Fin</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>

        {loadingPopups ? (
          <div className="crm-modales__empty">
            <Spinner animation="border" size="sm" className="me-2" /> Cargando Pop-Ups...
          </div>
        ) : errorPopups ? (
          <div className="crm-modales__empty is-error">{errorPopups}</div>
        ) : popups.length ? (
          <div className="crm-modales__rows">
            {popups.map((popup) => (
              <div className="crm-modales__row crm-modales__row--con-nombre" key={popup.id}>
                <span className="crm-modales__nombre">{popup.nombre || "Sin nombre"}</span>
                <span>
                  <span className="crm-modales__chip">{popup.tipo}</span>
                </span>
                <span>{popup.id}</span>
                <span>{formatFechaHora(popup.fechaInicio)}</span>
                <span>{formatFechaHora(popup.fechaFin)}</span>
                <span>
                  <span className={`crm-modales__estado ${popup.activoHoy ? "is-activo" : "is-finalizado"}`}>
                    {popup.activoHoy ? "Activo" : "Finalizado"}
                  </span>
                </span>
                <span className="crm-modales__row-actions">
                  <button type="button" className="crm-modales__edit-btn" onClick={() => abrirEdicionPopup(popup)}>
                    <FontAwesomeIcon icon={faPen} /> Editar
                  </button>
                  <button
                    type="button"
                    className="crm-modales__delete-icon"
                    onClick={() => handleEliminarPopup(popup)}
                    disabled={deletingPopupId === popup.id}
                    aria-label="Eliminar Pop-Up"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="crm-modales__empty">No hay Pop-Ups registrados.</div>
        )}
      </section>

      {/* ---------- Crear Notificación / Para aliados ---------- */}
      <div className="crm-modales__bottom-grid">
        <section className="crm-modales__card crm-modales__notif-card">
          <div className="crm-modales__card-header">
            <h3>Crear Notificación</h3>
            <button type="button" className="crm-modales__btn-primary" onClick={handleEnviarNotificacion} disabled={sendingNotif}>
              <FontAwesomeIcon icon={faPaperPlane} /> {sendingNotif ? "Enviando..." : "Enviar"}
            </button>
          </div>

          <form className="crm-modales__notif-form" onSubmit={handleEnviarNotificacion}>
            <label className="crm-modales__field">
              <span>Asunto</span>
              <input
                type="text"
                value={notifForm.asunto}
                onChange={(event) => setNotifForm((prev) => ({ ...prev, asunto: event.target.value }))}
              />
            </label>

            <label className="crm-modales__field crm-modales__field--textarea">
              <span>Mensaje para aliados</span>
              <textarea
                placeholder="Escribe aquí..."
                value={notifForm.mensaje}
                onChange={(event) => setNotifForm((prev) => ({ ...prev, mensaje: event.target.value }))}
              />
            </label>

            <label className="crm-modales__push-option">
              <input
                type="checkbox"
                checked={notifForm.enviarPush}
                onChange={(event) =>
                  setNotifForm((prev) => ({ ...prev, enviarPush: event.target.checked }))
                }
              />
              <span>Enviar también como notificación push a la app</span>
            </label>

            {notifForm.enviarPush ? (
              <label className="crm-modales__field">
                <span>CUIT destino (opcional; vacío envía a todos)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={13}
                  placeholder="Ej. 20123456789"
                  value={notifForm.cuitDestino}
                  onChange={(event) =>
                    setNotifForm((prev) => ({
                      ...prev,
                      cuitDestino: event.target.value.replace(/[^0-9-]/g, ""),
                    }))
                  }
                />
              </label>
            ) : null}
          </form>
        </section>

        <section className="crm-modales__card crm-modales__aliados-card">
          <div className="crm-modales__card-header">
            <h3>Para aliados</h3>
          </div>

          {loadingNotificaciones ? (
            <div className="crm-modales__empty">
              <Spinner animation="border" size="sm" className="me-2" /> Cargando publicaciones...
            </div>
          ) : errorNotificaciones ? (
            <div className="crm-modales__empty is-error">{errorNotificaciones}</div>
          ) : notificaciones.length ? (
            <>
              <div className="crm-modales__aliados-list">
                {visibleNotificaciones.map((item) => {
                  const { asunto, mensaje } = splitAsuntoMensaje(item.noticia1);
                  return (
                    <article className="crm-modales__publicacion" key={item.id}>
                      <div className="crm-modales__publicacion-actions">
                        <button type="button" className="crm-modales__mini-action" onClick={() => abrirEdicionNotif(item)}>
                          <FontAwesomeIcon icon={faPen} /> Editar
                        </button>
                        <button
                          type="button"
                          className="crm-modales__mini-action is-delete"
                          onClick={() => handleEliminarNotif(item)}
                          disabled={deletingNotifId === item.id}
                          aria-label="Eliminar publicación"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      {asunto ? <h4>{asunto}</h4> : null}
                      <p>{mensaje}</p>
                    </article>
                  );
                })}
              </div>

              {notificaciones.length > 3 ? (
                <button type="button" className="crm-modales__ver-mas" onClick={() => setParaAliadosExpanded((prev) => !prev)}>
                  {paraAliadosExpanded ? "Ver menos" : "Ver más"}
                  <FontAwesomeIcon icon={faChevronDown} className={paraAliadosExpanded ? "is-rotated" : ""} />
                </button>
              ) : null}
            </>
          ) : (
            <div className="crm-modales__empty">No hay publicaciones para aliados.</div>
          )}
        </section>
      </div>

      {/* ---------- Modal: Editar Pop-Up ---------- */}
      {editModalOpen ? (
        <div className="crm-modales__modal-overlay" onClick={cerrarModalEdicion}>
          <div className="crm-modales__modal crm-modales__modal--popup" onClick={(event) => event.stopPropagation()}>
            <div className="crm-modales__modal-header">
              <h3>Editar modal</h3>
              <button type="button" className="crm-modales__close-btn" onClick={cerrarModalEdicion} aria-label="Cerrar">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {editLoading ? (
              <div className="crm-modales__empty">
                <Spinner animation="border" size="sm" className="me-2" /> Cargando Pop-Up...
              </div>
            ) : (
              <form className="crm-modales__edit-form" onSubmit={handleGuardarEdicionPopup}>
                <div className="crm-modales__popup-fields">
                  <label className="crm-modales__field">
                    <span>Nombre del modal</span>
                    <input
                      type="text"
                      value={editForm.nombre}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, nombre: event.target.value }))}
                    />
                  </label>

                  <label className="crm-modales__field">
                    <span>Tipo</span>
                    <select
                      value={editForm.tipo}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, tipo: event.target.value }))}
                    >
                      {TIPOS_POPUP.map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="crm-modales__field">
                    <span>Fecha de inicio</span>
                    <input
                      type="date"
                      value={editForm.fechaInicio}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, fechaInicio: event.target.value }))}
                    />
                  </label>

                  <label className="crm-modales__field">
                    <span>Hora de inicio</span>
                    <input
                      type="time"
                      value={editForm.horaInicio}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, horaInicio: event.target.value }))}
                    />
                  </label>

                  <label className="crm-modales__field">
                    <span>Fecha de finalización</span>
                    <input
                      type="date"
                      value={editForm.fechaFin}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, fechaFin: event.target.value }))}
                    />
                  </label>

                  <label className="crm-modales__field">
                    <span>Hora de finalización</span>
                    <input
                      type="time"
                      value={editForm.horaFin}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, horaFin: event.target.value }))}
                    />
                  </label>
                </div>

                <input ref={editFileInputRef} type="file" accept="image/*" hidden onChange={handleEditArchivoChange} />

                {editPreview ? (
                  <button
                    type="button"
                    className="crm-modales__preview-box"
                    onClick={() => editFileInputRef.current?.click()}
                    title="Click para reemplazar la imagen"
                  >
                    <img src={editPreview} alt={editForm.nombre || "Vista previa del Pop-Up"} />
                    <span className="crm-modales__preview-overlay">Cambiar imagen</span>
                  </button>
                ) : (
                  <button type="button" className="crm-modales__preview-box is-empty" onClick={() => editFileInputRef.current?.click()}>
                    <FontAwesomeIcon icon={faArrowUpFromBracket} />
                    <span>Sin imagen disponible — click para seleccionar una</span>
                  </button>
                )}

                <p className="crm-modales__upload-note">
                  | Cargá una imagen acorde al tipo seleccionado.
                  <br />* PC {RECOMENDACIONES_POR_TIPO.PC} | CEL {RECOMENDACIONES_POR_TIPO.CEL} | TABLET {RECOMENDACIONES_POR_TIPO.TABLET}
                </p>

                <div className="crm-modales__modal-actions">
                  <button type="submit" className="crm-modales__btn-primary" disabled={editSaving}>
                    {editSaving ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}

      {/* ---------- Modal: Editar notificación ---------- */}
      {editingNotif ? (
        <div className="crm-modales__modal-overlay" onClick={() => setEditingNotif(null)}>
          <div className="crm-modales__modal crm-modales__modal--notif" onClick={(event) => event.stopPropagation()}>
            <div className="crm-modales__modal-header">
              <h3>Editar notificación</h3>
              <button type="button" className="crm-modales__close-btn" onClick={() => setEditingNotif(null)} aria-label="Cerrar">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form className="crm-modales__notif-form" onSubmit={handleGuardarEdicionNotif}>
              <label className="crm-modales__field">
                <span>Asunto</span>
                <input
                  type="text"
                  value={editingNotif.asunto}
                  onChange={(event) => setEditingNotif((prev) => ({ ...prev, asunto: event.target.value }))}
                />
              </label>
              <label className="crm-modales__field crm-modales__field--textarea">
                <span>Mensaje para aliados</span>
                <textarea
                  placeholder="Escribe aquí..."
                  value={editingNotif.mensaje}
                  onChange={(event) => setEditingNotif((prev) => ({ ...prev, mensaje: event.target.value }))}
                />
              </label>
              <div className="crm-modales__modal-actions">
                <button type="submit" className="crm-modales__btn-primary" disabled={savingNotifEdit}>
                  {savingNotifEdit ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
